import React, { useEffect, useState } from 'react';
import TrendLineChartModal from '../../Strategy/CreateStrategy/TrendLineChartModal';
import { TriggerTypeSelector } from '../Selectors';
import { generateId } from '../../../utils/strategyUtils';

// SVG Icons
const UpIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
  </svg>
);

const DownIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-error" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
  </svg>
);

const DrawIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const CheckCircleIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-success" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

/**
 * TrendLineConditionBuilder - Form for building trend line conditions
 * 
 * @param {Object} props
 * @param {Object} props.condition - Current condition data
 * @param {Function} props.onChange - Change handler (updatedCondition) => void
 * @param {Object} props.errors - Validation errors
 * @param {boolean} props.disabled - Whether form is disabled
 * @param {Array} props.availableTrendLines - Pre-defined trend lines to select from
 * @param {Function} props.onDrawTrendLine - Handler to trigger drawing on chart
 * @param {Object} props.rulesSchema - Rules schema from API (optional)
 * @param {string} props.tickerSymbol - Stock ticker symbol for the chart
 */
const ShowChartIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" />
  </svg>
);

const TrendLineConditionBuilder = ({
  condition,
  onChange,
  errors = {},
  disabled = false,
  availableTrendLines = [],
  rulesSchema = null,
  tickerSymbol = 'TITAN',
  onDrawNew = null
}) => {
  const [localCondition, setLocalCondition] = useState(condition || {
    conditionType: 'TRENDLINE',
    trendLine: {
      name: '',
      id: generateId('trendline'),
      x1: null,
      y1: null,
      x2: null,
      y2: null,
    },
    triggerType: 'PRICE_TOUCHED_TREND_LINE',
  });

  // State for chart modal
  const [chartModalOpen, setChartModalOpen] = useState(false);

  // Sync with parent
  useEffect(() => {
    if (condition) {
      setLocalCondition(condition);
    }
  }, [condition]);

  // Handle trigger type change
  const handleTriggerTypeChange = (triggerType) => {
    const updated = { ...localCondition, triggerType };
    setLocalCondition(updated);
    onChange(updated);
  };

  // Handle trend line field change
  const handleTrendLineChange = (field, value) => {
    const updated = {
      ...localCondition,
      trendLine: {
        ...localCondition.trendLine,
        [field]: value,
      },
    };
    setLocalCondition(updated);
    onChange(updated);
  };

  // Handle selecting a pre-defined trend line
  const handleSelectTrendLine = (lineId) => {
    const selectedLine = availableTrendLines.find(line => line.id === lineId);
    if (selectedLine) {
      const updated = {
        ...localCondition,
        trendLine: {
          id: selectedLine.id,
          name: selectedLine.name || `Trend Line`,
          x1: selectedLine.x1,
          y1: selectedLine.y1,
          x2: selectedLine.x2,
          y2: selectedLine.y2,
        },
      };
      setLocalCondition(updated);
      onChange(updated);
    }
  };

  // Handle trendline selected from chart modal
  const handleTrendLineFromChart = (trendLine) => {
    const updated = {
      ...localCondition,
      trendLine: {
        id: trendLine.id,
        name: trendLine.name || `Trend Line`,
        x1: trendLine.x1,
        y1: trendLine.y1,
        x2: trendLine.x2,
        y2: trendLine.y2,
      },
    };
    setLocalCondition(updated);
    onChange(updated);
    setChartModalOpen(false);
  };

  // Format timestamp for display
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  // Get trend direction
  const getTrendDirection = (line) => {
    if (!line?.y1 || !line?.y2) return null;
    return line.y2 > line.y1 ? 'up' : 'down';
  };

  // Check if trend line has coordinates
  const hasCoordinates = localCondition.trendLine?.x1 && localCondition.trendLine?.y1;

  return (
    <div>
      <h4 className="text-sm font-medium text-base-content/70 mb-2">
        Trend Line Condition
      </h4>
      <div className="divider my-2"></div>

      <div className="space-y-4">
        {/* Draw on Chart Button */}
        <div className={`p-4 rounded-lg border-2 border-dashed ${hasCoordinates ? 'bg-success/10 border-success' : 'bg-primary/10 border-primary'}`}>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-2">
              {hasCoordinates ? <CheckCircleIcon /> : <DrawIcon />}
              <div>
                <p className="font-medium text-sm">
                  {hasCoordinates ? 'Trend Line Configured' : 'Draw Trend Line on Chart'}
                </p>
                <p className="text-xs text-base-content/60">
                  {hasCoordinates
                    ? `${localCondition.trendLine?.name || 'Trend Line'}: ₹${localCondition.trendLine?.y1?.toFixed(2)} → ₹${localCondition.trendLine?.y2?.toFixed(2)}`
                    : `Open chart for ${tickerSymbol} to draw a trend line`
                  }
                </p>
              </div>
            </div>
            <button
              className={`btn btn-sm gap-1 ${hasCoordinates ? 'btn-outline btn-success' : 'btn-primary'}`}
              onClick={() => setChartModalOpen(true)}
              disabled={disabled}
            >
              <DrawIcon />
              {hasCoordinates ? 'Redraw' : 'Draw on Chart'}
            </button>
          </div>
        </div>

        {/* Select from available trend lines if any */}
        {availableTrendLines.length > 0 && (
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Select from Previously Drawn</span>
            </label>
            <div className="flex gap-2">
              <select
                className={`select select-bordered bg-base-200 w-full ${errors.trendLineId ? 'select-error' : ''}`}
                value={localCondition.trendLine?.id || ''}
                onChange={(e) => handleSelectTrendLine(e.target.value)}
                disabled={disabled}
              >
                <option value="">-- Draw New --</option>
                {availableTrendLines.map((line, index) => (
                  <option key={line.id || index} value={line.id}>
                    {line.name || `Trend Line ${index + 1}`} (₹{line.y1?.toFixed(0)} → ₹{line.y2?.toFixed(0)})
                  </option>
                ))}
              </select>
              <button
                className="btn btn-square btn-outline border-dashed tooltip tooltip-left"
                data-tip="Draw on Chart"
                onClick={() => setChartModalOpen(true)}
                disabled={disabled}
              >
                <DrawIcon />
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Trend Line Name */}
          <div className="fieldset w-full">
            <label className="label">
              <span className="text-sm">Trend Line Name</span>
            </label>
            <input
              type="text"
              className={`input input-bordered bg-base-200 w-full ${errors.name ? 'input-error' : ''}`}
              value={localCondition.trendLine?.name || ''}
              onChange={(e) => handleTrendLineChange('name', e.target.value)}
              disabled={disabled}
              placeholder="e.g., Support Line, Resistance"
            />
            {(errors.name || availableTrendLines.length > 0) && (
              <label className="label">
                <span className={`text-xs text-base-content/60 ${errors.name ? 'text-error' : ''}`}>
                  {errors.name || 'Or enter a custom name'}
                </span>
              </label>
            )}
          </div>

          {/* Trigger Type */}
          <TriggerTypeSelector
            value={localCondition.triggerType}
            onChange={handleTriggerTypeChange}
            error={errors.triggerType}
            disabled={disabled}
            required
            rulesSchema={rulesSchema}
          />
        </div>

        {/* Coordinates Display */}
        <div className="p-4 rounded-lg bg-base-200 border border-base-300">
          <p className="text-xs text-base-content/60 mb-2">Trend Line Coordinates</p>

          {hasCoordinates ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium">Start Point:</p>
                <p className="text-xs text-base-content/60">
                  Time: {formatTimestamp(localCondition.trendLine.x1)}
                </p>
                <p className="text-xs text-base-content/60">
                  Price: {localCondition.trendLine.y1?.toFixed(2)}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium">End Point:</p>
                <p className="text-xs text-base-content/60">
                  Time: {formatTimestamp(localCondition.trendLine.x2)}
                </p>
                <p className="text-xs text-base-content/60">
                  Price: {localCondition.trendLine.y2?.toFixed(2)}
                </p>
              </div>
            </div>
          ) : (
            <div className="alert alert-info mt-2">
              <span className="text-sm">Draw a trend line on the chart to set coordinates, or enter them manually below.</span>
            </div>
          )}
        </div>

        {/* Manual Coordinate Entry */}
        <div>
          <p className="text-xs text-base-content/60 mb-2">Manual Entry (optional)</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            <div className="fieldset">
              <label className="label"><span className="text-sm text-xs">Start Price (Y1)</span></label>
              <input
                type="number"
                className={`input input-bordered bg-base-200 input-sm w-full ${errors.y1 ? 'input-error' : ''}`}
                value={localCondition.trendLine?.y1 ?? ''}
                onChange={(e) => handleTrendLineChange('y1', e.target.value ? parseFloat(e.target.value) : null)}
                disabled={disabled}
                step="0.01"
              />
            </div>
            <div className="fieldset">
              <label className="label"><span className="text-sm text-xs">End Price (Y2)</span></label>
              <input
                type="number"
                className={`input input-bordered bg-base-200 input-sm w-full ${errors.y2 ? 'input-error' : ''}`}
                value={localCondition.trendLine?.y2 ?? ''}
                onChange={(e) => handleTrendLineChange('y2', e.target.value ? parseFloat(e.target.value) : null)}
                disabled={disabled}
                step="0.01"
              />
            </div>
            <div className="fieldset">
              <label className="label"><span className="text-sm text-xs">Start Time (X1)</span></label>
              <input
                type="datetime-local"
                className={`input input-bordered bg-base-200 input-sm w-full ${errors.x1 ? 'input-error' : ''}`}
                value={localCondition.trendLine?.x1 ? new Date(localCondition.trendLine.x1).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleTrendLineChange('x1', e.target.value ? new Date(e.target.value).getTime() : null)}
                disabled={disabled}
              />
            </div>
            <div className="fieldset">
              <label className="label"><span className="text-sm text-xs">End Time (X2)</span></label>
              <input
                type="datetime-local"
                className={`input input-bordered bg-base-200 input-sm w-full ${errors.x2 ? 'input-error' : ''}`}
                value={localCondition.trendLine?.x2 ? new Date(localCondition.trendLine.x2).toISOString().slice(0, 16) : ''}
                onChange={(e) => handleTrendLineChange('x2', e.target.value ? new Date(e.target.value).getTime() : null)}
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* Available Trend Lines (if any) */}
        {availableTrendLines.length > 0 && (
          <div className="alert alert-info">
            <span className="text-sm">
              {availableTrendLines.length} trend line(s) available from previous drawings.
              Select one or draw a new one.
            </span>
          </div>
        )}
      </div>

      {/* Chart Modal for Drawing Trend Lines */}
      <TrendLineChartModal
        open={chartModalOpen}
        onClose={() => setChartModalOpen(false)}
        symbol={tickerSymbol}
        onSelectTrendLine={handleTrendLineFromChart}
      />
    </div>
  );
};

export default TrendLineConditionBuilder;
